import { getCategoryUnits, Unit, UnitCategory } from '../units';
import { ApplicationError } from '../../helpers';


/**
 * Function type for unit conversions that require a formula.
 */
declare type CONVERSION_FUNCTION = (value: number) => number;


/**
 * Unit conversion error type
 */
export class UnitConversionError extends ApplicationError {}


/**
 * Asbtract unit converter class that has functionality for converting between units within a single category (length,
 * temperature, etc.). There is a subclass for each category (LengthConverter, TemperatureConverter, etc.) that defines
 * the conversion factors/methods for that category.
 */
export abstract class BaseConverter<T extends Unit> {
  /**
   * Base unit from which all conversions are derived. When converting a value from unit U1 to unit U2, the value will
   * first be converted to the base unit (B), then to the desired destination unit (U1 -> B -> U2). This is used to
   * minimize the number of conversion factors/formulae that need to be defined, allowing us to maintain a single list
   * instead of an NxN table.
   */
  protected abstract _baseUnit: T;

  /**
   * Map of conversion factors from the base unit to other units. In case the unit conversion requires a formula rather
   * than a conversion factor, the _baseConversionMethods and _reverseConversionFunctions maps should be used instead.
   */
  protected _conversionFactors: Map<T, number> = new Map<T, number>();

  /**
   * Map of conversion functions from the base unit to other units. This is used when the unit conversion cannot be
   * expressed as a simple conversion factor (e.g., converting Celsius to Fahrenheit or vice-versa). For conversion
   * functions, both the base function (base unit to other units, B -> U) and the reverse function (U -> B) must be
   * defined separately.
   */
  protected _baseConversionFunctions: Map<T, CONVERSION_FUNCTION> = new Map<T, CONVERSION_FUNCTION>();

  /**
   * Map of conversion functions from a given unit to the base unit (this is the reverse mapping of
   * _baseConversionFunctions).
   */
  protected _reverseConversionFunctions: Map<T, CONVERSION_FUNCTION> = new Map<T, CONVERSION_FUNCTION>();

  /**
   * Constructor
   * @param {UnitCategory} _unitCategory Unit category of the units this converter is capable of converting.
   */
  constructor(protected _unitCategory: UnitCategory) {}

  /**
   * Converts a value from one unit to another
   * @param {number} value Value to convert
   * @param {T} from Source unit
   * @param {T} to Destination unit
   * @return {number} Converted value
   */
  convert(value: number, from: T, to: T): number {
    const base = this._convertToBaseUnit(value, from);
    return this._convertFromBaseUnit(base, to);
  }

  /**
   * Checks to ensure that a conversion factor or formula has been defined for each unit in the category. For formulas,
   * ensures that both the forward and reverse conversion functions have been defined. This method should be called at
   * the end of the constructor of all derived types.
   * @private
   */
  protected _check() {
    for (const unitStr of getCategoryUnits(this._unitCategory)) {
      const unit: T = unitStr as T;
      if (unit !== this._baseUnit && !(this._conversionFactors.has(unit)
          || (this._baseConversionFunctions.has(unit) && this._reverseConversionFunctions.has(unit)))) {
        throw new Error(`Missing conversion factor/formula for unit "${unit}" in converter for category "${this._unitCategory}". `
          + `The converter must define either a single conversion factor (in _conversionFactors) or a pair of `
          + `conversion formulas (in _baseConversionFunctions and _reverseConversionFunctions) for each unit `
          + `in the category.`);
      }
    }
  }

  /**
   * Converts a value from a given unit to the base unit.
   * @param {number} value Value to convert (expressed in the unit specified by the unit parameter).
   * @param {T} unit Unit of the value.
   * @return {number} Converted value expressed in the base unit.
   * @private
   */
  protected _convertToBaseUnit(value: number, unit: T) {
    if (unit === this._baseUnit) {
      return value;
    }
    if (this._reverseConversionFunctions.has(unit)) {
      const formula = this._reverseConversionFunctions.get(unit);
      return formula(value);
    }
    if (this._conversionFactors.has(unit)) {
      const factor = this._conversionFactors.get(unit);
      return value / factor;
    }
    throw new UnitConversionError(`No conversion factor or formula found for converting a value from unit "${unit}`
      + ` to the base unit "${this._baseUnit}".`);
  }

  /**
   * Converts a value from the base unit to a given unit.
   * @param {number} value Value to convert (expressed in the base unit).
   * @param {T} unit Unit the value should be converted to.
   * @return {number} Converted value (expressed in the unit specified by the unit parameter).
   * @private
   */
  protected _convertFromBaseUnit(value: number, unit: T) {
    if (unit === this._baseUnit) {
      return value;
    }
    if (this._baseConversionFunctions.has(unit)) {
      const formula = this._baseConversionFunctions.get(unit);
      return formula(value);
    }
    if (this._conversionFactors.has(unit)) {
      const factor = this._conversionFactors.get(unit);
      return value * factor;
    }
    throw new UnitConversionError(`No conversion factor or formula found for converting a value from the base `
      + `unit "${this._baseUnit}" to unit "${unit}".`);
  }
}
